import React from 'react';
import Select, { SingleValue, StylesConfig, Props } from 'react-select';
import { GenericObject } from 'v2/models';
export type { SingleValue } from 'react-select';

export interface OptionType<T = string | boolean | number> {
  label: string;
  value: T;
}

type handleChangeType<T> = (selectedOption: SingleValue<T>) => void;

export type GroupedOptions<T> = {
  label: string;
  options: T[];
};

interface propsTypes<T extends OptionType = OptionType> {
  options: (T | GroupedOptions<T>)[];
  selectedOption?: SingleValue<T>;
  handleChange: handleChangeType<T>;
  placeHolder?: string;
  disabled?: boolean;
  customStyles?: GenericObject;
  fullWidth?: boolean;
  isClearable?: boolean;
  components?: Props<T, false>['components'];
  menuIsOpen?: Props<T, false>['menuIsOpen'];
  'aria-label'?: Props<T, false>['aria-label'];
}

export function GeneralSelect<T extends OptionType>({
  options,
  selectedOption,
  handleChange,
  placeHolder,
  disabled,
  customStyles,
  fullWidth,
  components,

  ...selectProps
}: propsTypes<T>) {
  const isOptionsGrouped = options.length > 0 && 'options' in options[0];

  const selectStyle: StylesConfig<T, false> = {
    option: (provided) => ({
      ...provided,
      overflowWrap: 'break-word',
      backgroundColor: 'white',
      color: 'var(--dark)',
      borderBottom: '1px solid #DDDDDD',
      ':last-child': {
        borderBottom: '0px',
      },
      padding: 10,
      paddingLeft: isOptionsGrouped ? 34 : 15,
      cursor: 'pointer',
      ':hover': {
        backgroundColor: '#DDDDDD',
      },
    }),
    groupHeading: (base) => ({
      ...base,
      fontWeight: 'bold',
      color: 'var(--dark)',
      fontSize: '16px',
    }),
    control: (provided) => ({
      display: 'flex',
      width: fullWidth ? '100%' : '210px',
      height: '50px',
      border: '1px solid transparent',
      borderRadius: '5px',
      borderColor: '#DDE5F7',
      padding: 10,
      paddingLeft: 4,
      backgroundColor: provided.backgroundColor,
      ...customStyles?.control,
    }),
    menuPortal: (base) => ({ ...base, zIndex: 50000 }),
    menu: (provided) => ({
      ...provided,
      boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.15)',
      marginTop: 4,
      borderRadius: '5px',
    }),
    dropdownIndicator: (base) => ({
      ...base,
      color: '#285FCB',
      ':hover': {
        color: '#285FCB',
      },
      svg: {
        boxSizing: 'unset',
      },
      ...customStyles?.dropdownIndicator,
    }),
    placeholder: (base, { isDisabled }) => ({
      ...base,
      color: isDisabled ? base.color : '#666666',
    }),
    menuList: (base) => ({
      ...base,
      paddingTop: 0,
      paddingBottom: 0,
      borderRadius: '5px',
      border: '1px solid #DDDDDD',
    }),
    input: (base) => ({
      ...base,
      width: '100%',
    }),
  };

  return (
    <div data-testid="general-select">
      <Select
        classNamePrefix="react-select"
        options={options}
        value={selectedOption?.label === '' ? null : selectedOption}
        onChange={handleChange}
        isSearchable={false}
        placeholder={placeHolder}
        styles={selectStyle}
        menuPortalTarget={document.body}
        isDisabled={disabled}
        components={{
          IndicatorSeparator: () => null,
          ...components,
        }}
        theme={(theme) => ({
          ...theme,
          colors: {
            ...theme.colors,
            primary50: 'transparent',
            primary: 'transparent',
          },
        })}
        {...selectProps}
      />
    </div>
  );
}
